import { styled } from "goober";
import { useMemo } from "react";
import type { ParsedStep } from "../lib/parseCommitData";
import { Button } from "./Button";
import {
  StatusBreakdownChart,
  type StatusSegment,
} from "./StatusBreakdownChart";

type Props = {
  steps: ReadonlyArray<ParsedStep>;
  onStepsHover?: (canonicalIds: ReadonlyArray<string> | null) => void;
  onInvestigateFailingSteps?: (stepNames: readonly string[]) => void;
};

type StepHealth = "healthy" | "failing" | "running" | "idle";

const STEP_STATUS: Array<{ key: StepHealth; color: string; label: string }> = [
  { key: "healthy", color: "var(--green-600)", label: "healthy" },
  { key: "failing", color: "var(--red-600)", label: "failing" },
  { key: "running", color: "var(--sky-600)", label: "running" },
  { key: "idle", color: "var(--gray-400)", label: "idle" },
];

function deriveStepHealth(step: ParsedStep): StepHealth {
  if (step.lastStatus === "success") return "healthy";
  if (step.lastStatus === "error") return "failing";
  if (step.lastStatus === "pending") return "running";
  return "idle";
}

const Outer = styled("div")`
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export function WorkflowStepsPanel({
  steps,
  onStepsHover,
  onInvestigateFailingSteps,
}: Props) {
  const segments: StatusSegment[] = useMemo(() => {
    const buckets: Record<StepHealth, string[]> = {
      healthy: [],
      failing: [],
      running: [],
      idle: [],
    };
    for (const s of steps) buckets[deriveStepHealth(s)].push(s.name);
    return STEP_STATUS.map((d) => ({
      key: d.key,
      count: buckets[d.key].length,
      color: d.color,
      label: d.label,
      items: buckets[d.key],
    }));
  }, [steps]);

  const failingNames = segments.find((s) => s.key === "failing")?.items ?? [];

  return (
    <Outer>
      <StatusBreakdownChart
        title="Step status"
        total={steps.length}
        segments={segments}
        onSegmentHover={(seg) =>
          onStepsHover?.(seg ? (seg.items ?? null) : null)
        }
        accessory={
          onInvestigateFailingSteps && failingNames.length > 0 ? (
            <Button
              variant="subtle"
              size="m"
              icon="traffic-cone"
              onClick={() => onInvestigateFailingSteps(failingNames)}
            >
              Fix failures
            </Button>
          ) : undefined
        }
      />
    </Outer>
  );
}
